import React, { useState, useRef } from "react";
import { Tabs, Button, message } from "antd";
import { 
  ShoppingCartOutlined, 
  GiftOutlined,
  SkinOutlined,
  HomeOutlined,
} from "@ant-design/icons";
import { useAccount } from "wagmi";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import "../styles/global-background.css";
import "../styles/MarketPage.css";
import "../styles/cards/index.css";
import "../styles/special-cards.css";

// 用户盲盒库存接口
interface UserBlindbox {
  id: string;
  itemId: string;
  name: string;
  image: string;
  series: string;
  description: string;
  purchaseDate: string;
  opened: boolean;
}

// 演示用皮肤数据接口
interface DemoSkinData {
  id: string;
  name: string;
  cardFront: string;
  cardBack: string;
  rarity: string;
}

const MarketPage: React.FC = () => {
  const [language, setLanguage] = useState<"en" | "zh">("zh");
  
  // 开盒相关状态
  const [selectedBlindbox, setSelectedBlindbox] = useState<UserBlindbox | null>(null);
  const [isOpening, setIsOpening] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const [revealedSkin, setRevealedSkin] = useState<DemoSkinData | null>(null);
  const [showCard, setShowCard] = useState(false);
  const [userBlindboxes, setUserBlindboxes] = useState<UserBlindbox[]>([]);
  
  // 演示用开盒状态
  const [isDemoOpening, setIsDemoOpening] = useState(false);
  const [demoHasOpened, setDemoHasOpened] = useState(false);
  const [demoRevealedSkin, setDemoRevealedSkin] = useState<DemoSkinData | null>(null);
  const [demoShowCard, setDemoShowCard] = useState(false);

  const { address } = useAccount();
  const openBoxCardRef = useRef<HTMLDivElement | null>(null);
  const demoCardRef = useRef<HTMLDivElement | null>(null);
  const skinDemoCardRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  
  // 演示用皮肤数据
  const demoSkins: DemoSkinData[] = [
    {
      id: "demo-1",
      name: "???",
      cardFront: "/images/skinCards/four_skin_cardback.webp",
      cardBack: "/images/skinCards/four_skin_cardback.webp",
      rarity: "legendary"
    },
    {
      id: "demo-2", 
      name: "???",
      cardFront: "/images/skinCards/four_skin_cardback.webp",
      cardBack: "/images/skinCards/four_skin_cardback.webp",
      rarity: "legendary"
    },
    {
      id: "demo-3",
      name: "???",
      cardFront: "/images/skinCards/four_skin_cardback.webp", 
      cardBack: "/images/skinCards/four_skin_cardback.webp",
      rarity: "legendary"
    }
  ];

  // 获取稀有度颜色
  const getRarityColor = (rarity: string = "common") => {
    switch (rarity) {
      case 'common': return '#7f7f7f';
      case 'uncommon': return '#1ab34a';
      case 'rare': return '#0070dd';
      case 'epic': return '#a335ee';
      case 'legendary': return '#ff8000';
      default: return '#7f7f7f';
    }
  };

  // 获取稀有度文本
  const getRarityText = (rarity: string = "common") => {
    const rarityMap: Record<string, string> = {
      'common': '普通',
      'uncommon': '优秀', 
      'rare': '稀有',
      'epic': '史诗',
      'legendary': '传说'
    };
    return rarityMap[rarity] || rarity;
  };

  // 演示开盒动画处理
  const handleDemoOpenBox = () => {
    if (isDemoOpening) return;

    setIsDemoOpening(true);
    setDemoHasOpened(false);
    setDemoShowCard(false);
    
    // 模拟开盒过程
    setTimeout(() => {
      // 随机选择一个演示皮肤
      const randomSkin = demoSkins[Math.floor(Math.random() * demoSkins.length)];
      setDemoRevealedSkin(randomSkin);
      setIsDemoOpening(false);
      
      // 延迟显示卡牌
      setTimeout(() => {
        setDemoShowCard(true);
        setDemoHasOpened(true);
        message.success(`恭喜获得 ${randomSkin.name}！`);
      }, 500);
    }, 3000);
  };

  // 重置演示开盒状态
  const resetDemoOpenBox = () => {
    setIsDemoOpening(false);
    setDemoHasOpened(false);
    setDemoRevealedSkin(null);
    setDemoShowCard(false);
  };

  // 卡牌交互处理（演示页面）
  const handleDemoCardInteraction = (e: React.MouseEvent<HTMLDivElement>, cardRef: React.RefObject<HTMLDivElement | null>) => {
    const card = cardRef.current;
    if (!card) return;

    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    const posX = Math.max(0, Math.min(100, (x / rect.width) * 100));
    const posY = Math.max(0, Math.min(100, (y / rect.height) * 100));
    
    const centerX = posX - 50;
    const centerY = posY - 50;
    
    const fromCenter = Math.min(1, Math.sqrt(centerX * centerX + centerY * centerY) / 50);
    
    card.classList.add('active', 'interacting');
    card.style.setProperty('--pointer-x', `${posX}%`);
    card.style.setProperty('--pointer-y', `${posY}%`);
    card.style.setProperty('--pointer-from-center', fromCenter.toString());
    card.style.setProperty('--rotate-x', `${-(centerX / 3.5)}deg`);
    card.style.setProperty('--rotate-y', `${centerY / 2}deg`);
    card.style.setProperty('--card-opacity', '1');
  };

  const handleDemoCardLeave = (cardRef: React.RefObject<HTMLDivElement | null>) => {
    const card = cardRef.current;
    if (!card) return;

    card.classList.remove('active', 'interacting');
    setTimeout(() => {
      card.style.setProperty('--pointer-x', '50%');
      card.style.setProperty('--pointer-y', '50%');
      card.style.setProperty('--pointer-from-center', '0');
      card.style.setProperty('--rotate-x', '0deg');
      card.style.setProperty('--rotate-y', '0deg');
      card.style.setProperty('--card-opacity', '0');
    }, 100);
  };

  // 处理真实开盒（我的盲盒tab中的逻辑保持不变）
  const handleOpenBox = (blindbox: UserBlindbox) => {
    if (isOpening || blindbox.opened) return;

    setSelectedBlindbox(blindbox);
    setIsOpening(true);
    setHasOpened(false);
    setShowCard(false);
    
    // 模拟开盒过程
    setTimeout(() => {
      // 随机选择一个皮肤
      const randomSkin = demoSkins[Math.floor(Math.random() * demoSkins.length)];
      setRevealedSkin(randomSkin);
      setIsOpening(false);
      
      // 延迟显示卡牌
      setTimeout(() => {
        setShowCard(true);
        setHasOpened(true);
        
        // 更新盲盒状态为已开启
        setUserBlindboxes(prev => 
          prev.map(box => 
            box.id === blindbox.id 
              ? { ...box, opened: true }
              : box
          )
        );
        
        message.success(`恭喜获得 ${randomSkin.name}！`);
      }, 500);
    }, 3000);
  };

  // 重置开盒状态
  const resetOpenBox = () => {
    setSelectedBlindbox(null);
    setIsOpening(false);
    setHasOpened(false);
    setRevealedSkin(null);
    setShowCard(false);
  };

  // 卡牌交互处理（开盒页面）
  const handleOpenBoxCardInteraction = (e: React.MouseEvent<HTMLDivElement>) => {
    const card = openBoxCardRef.current;
    if (!card) return;

    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    const posX = Math.max(0, Math.min(100, (x / rect.width) * 100));
    const posY = Math.max(0, Math.min(100, (y / rect.height) * 100));
    
    const centerX = posX - 50;
    const centerY = posY - 50;
    
    const fromCenter = Math.min(1, Math.sqrt(centerX * centerX + centerY * centerY) / 50);
    
    card.classList.add('active', 'interacting');
    card.style.setProperty('--pointer-x', `${posX}%`);
    card.style.setProperty('--pointer-y', `${posY}%`);
    card.style.setProperty('--pointer-from-center', fromCenter.toString());
    card.style.setProperty('--rotate-x', `${-(centerX / 3.5)}deg`);
    card.style.setProperty('--rotate-y', `${centerY / 2}deg`);
    card.style.setProperty('--card-opacity', '1');
  };

  const handleOpenBoxCardLeave = () => {
    const card = openBoxCardRef.current;
    if (!card) return;

    card.classList.remove('active', 'interacting');
    setTimeout(() => {
      card.style.setProperty('--pointer-x', '50%');
      card.style.setProperty('--pointer-y', '50%');
      card.style.setProperty('--pointer-from-center', '0');
      card.style.setProperty('--rotate-x', '0deg');
      card.style.setProperty('--rotate-y', '0deg');
      card.style.setProperty('--card-opacity', '0');
    }, 100);
  };

  // 渲染盲盒演示内容
  const renderBlindboxDemo = () => {
    if (demoHasOpened && demoRevealedSkin) {
      return (
        <div className="open-box-result">
          <div className="result-header">
            <h2 className="congratulations-title">🎉 恭喜获得稀有皮肤！</h2>
            <p>您从神秘盲盒中获得了：</p>
          </div>

          <div className={`revealed-card-container ${demoShowCard ? 'show' : ''}`}>
            <div className="skin-info-display">
              <h3 className="skin-name">{demoRevealedSkin.name}</h3>
              <div 
                className="skin-rarity" 
                style={{ color: getRarityColor(demoRevealedSkin.rarity) }}
              >
                {getRarityText(demoRevealedSkin.rarity)}
              </div>
            </div>

            <div 
              ref={demoCardRef}
              className="card revealed-card"
              data-rarity={demoRevealedSkin.rarity.toLowerCase()}
              data-subtypes="skin"
              data-supertype="cosmetic"
              onMouseMove={(e) => handleDemoCardInteraction(e, demoCardRef)}
              onMouseLeave={() => handleDemoCardLeave(demoCardRef)}
            >
              <div className="card__translater">
                <div className="card__rotator">
                  <img
                    className="card__back"
                    src={demoRevealedSkin.cardBack}
                    alt="Card back"
                    loading="lazy"
                    width="660"
                    height="921"
                  />
                  <div className="card__front">
                    <img 
                      src={demoRevealedSkin.cardFront} 
                      alt={demoRevealedSkin.name}
                      className="card__artwork"
                      loading="lazy"
                      width="660"
                      height="921"
                    />
                    <span className="card__name">{demoRevealedSkin.name}</span>
                    <div className="card__shine"></div>
                    <div className="card__glare"></div>
                  </div>
                </div>
              </div>
            </div>

            <div className="result-actions">
              <Button 
                size="large"
                onClick={resetDemoOpenBox}
                className="continue-btn"
              >
                返回
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="demo-section">
        <div className="coming-soon-banner">
          <h3>🚀 即将推出</h3>
          <p>系列盲盒即将推出，敬请期待！</p>
        </div>
        
        <div className="demo-content">
          
          <div className="demo-blindbox-container">
            <div className={`dynamic-blindbox demo ${isDemoOpening ? 'opening' : ''}`}>
              <div className="blindbox-emoji">
                📦
              </div>
              
              <div className="blindbox-particles">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className={`particle particle-${i + 1}`}></div>
                ))}
              </div>
              
              <div className="blindbox-glow"></div>
              
              <div className="blindbox-details">
                
                <Button
                  type="primary"
                  icon={<GiftOutlined />}
                  onClick={handleDemoOpenBox}
                  disabled={isDemoOpening}
                  className="open-blindbox-btn"
                  size="large"
                >
                  {isDemoOpening ? '开启中...' : '打开'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // 渲染皮肤演示内容
  const renderSkinDemo = () => {
    return (
      <div className="demo-section">
        <div className="coming-soon-banner">
          <h3>🚀 即将推出</h3>
          <p>限定皮肤即将推出，敬请期待！</p>
        </div>
        
        <div className="demo-content">
          <div className="skin-demo-container">
            <div 
              ref={skinDemoCardRef}
              className="card demo-skin-card"
              data-rarity="legendary"
              data-subtypes="skin"
              data-supertype="cosmetic"
              onMouseMove={(e) => handleDemoCardInteraction(e, skinDemoCardRef)}
              onMouseLeave={() => handleDemoCardLeave(skinDemoCardRef)}
            >
              <div className="card__translater">
                <div className="card__rotator">
                  <img
                    className="card__back"
                    src="/images/skinCards/four_skin_cardback.webp"
                    alt="Card back"
                    loading="lazy"
                    width="660"
                    height="921"
                  />
                  <div className="card__front">
                    <img 
                      src="/images/skinCards/four_skin_cardback.webp" 
                      alt="????"
                      className="card__artwork"
                      loading="lazy"
                      width="660"
                      height="921"
                    />
                    <div className="card__shine"></div>
                    <div className="card__glare"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // 渲染动态盲盒卡片
  const renderDynamicBlindbox = (box: UserBlindbox, index: number) => {
    const delay = index * 0.1;
    
    return (
      <div key={box.id} className="user-blindbox-item" style={{ animationDelay: `${delay}s` }}>
        <div className={`dynamic-blindbox ${box.opened ? 'opened' : ''} ${isOpening && selectedBlindbox?.id === box.id ? 'opening' : ''}`}>
          <div className="blindbox-emoji">
            {box.opened ? '📭' : '📦'}
          </div>
          
          <div className="blindbox-particles">
            {[...Array(6)].map((_, i) => (
              <div key={i} className={`particle particle-${i + 1}`}></div>
            ))}
          </div>
          
          <div className="blindbox-glow"></div>
          
          <div className="blindbox-details">
            <h3>{box.name}</h3>
            <p className="blindbox-series">{box.series}</p>
            <p className="blindbox-date">购买日期: {box.purchaseDate}</p>
            
            {box.opened ? (
              <div className="opened-status">
                <span>已开启</span>
              </div>
            ) : (
              <Button
                type="primary"
                icon={<GiftOutlined />}
                onClick={() => handleOpenBox(box)}
                disabled={isOpening}
                className="open-blindbox-btn"
              >
                {isOpening && selectedBlindbox?.id === box.id ? '开启中...' : '开启盲盒'}
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // 渲染我的盲盒tab内容
  const renderMyBlindboxes = () => {
    const availableBoxes = userBlindboxes.filter(box => !box.opened);
    const openedBoxes = userBlindboxes.filter(box => box.opened);

    if (!address) {
      return (
        <div className="no-wallet-connected">
          <h3>请先连接钱包</h3>
          <p>连接钱包后即可查看您的盲盒库存</p>
        </div>
      );
    }

    if (userBlindboxes.length === 0) {
      return (
        <div className="empty-blindbox-inventory">
          <h3>暂无盲盒</h3>
          <p>前往商店购买盲盒开始您的收藏之旅</p>
          <Button type="primary" onClick={() => window.scrollTo(0, 0)}>
            前往购买
          </Button>
        </div>
      );
    }

    if (hasOpened && revealedSkin) {
      return (
        <div className="open-box-result">
          <div className="result-header">
            <h2 className="congratulations-title">🎉 恭喜获得稀有皮肤！</h2>
            <p>您从 {selectedBlindbox?.name} 中获得了：</p>
          </div>

          <div className={`revealed-card-container ${showCard ? 'show' : ''}`}>
            <div className="skin-info-display">
              <h3 className="skin-name">{revealedSkin.name}</h3>
              <div 
                className="skin-rarity" 
                style={{ color: getRarityColor(revealedSkin.rarity) }}
              >
                {getRarityText(revealedSkin.rarity)}
              </div>
            </div>

            <div 
              ref={openBoxCardRef}
              className="card revealed-card"
              data-rarity={revealedSkin.rarity.toLowerCase()}
              data-subtypes="skin"
              data-supertype="cosmetic"
              onMouseMove={handleOpenBoxCardInteraction}
              onMouseLeave={handleOpenBoxCardLeave}
            >
              <div className="card__translater">
                <div className="card__rotator">
                  <img
                    className="card__back"
                    src={revealedSkin.cardBack}
                    alt="Card back"
                    loading="lazy"
                    width="660"
                    height="921"
                  />
                  <div className="card__front">
                    <img 
                      src={revealedSkin.cardFront} 
                      alt={revealedSkin.name}
                      className="card__artwork"
                      loading="lazy"
                      width="660"
                      height="921"
                    />
                    <span className="card__name">{revealedSkin.name}</span>
                    <div className="card__shine"></div>
                    <div className="card__glare"></div>
                  </div>
                </div>
              </div>
            </div>

            <div className="result-actions">
              <Button 
                size="large"
                onClick={resetOpenBox}
                className="continue-btn"
              >
                继续开盒
              </Button>
              <Button 
                type="primary"
                size="large"
                icon={<HomeOutlined />}
                onClick={() => navigate('/')}
              >
                返回首页
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="blindbox-inventory">
        {availableBoxes.length > 0 && (
          <div className="blindbox-section">
            <h3>可开启的盲盒 ({availableBoxes.length})</h3>
            <div className="blindbox-grid-inventory">
              {availableBoxes.map((box, index) => renderDynamicBlindbox(box, index))}
            </div>
          </div>
        )}

        {openedBoxes.length > 0 && (
          <div className="blindbox-section">
            <h3>已开启的盲盒 ({openedBoxes.length})</h3>
            <div className="blindbox-grid-inventory">
              {openedBoxes.map((box, index) => renderDynamicBlindbox(box, index))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="nova-container market-page">
      <div className="nova-grid"></div>
      
      <Navbar language={language} />

      <div className="content">
        <div className="market-title-section">
          <h1 className="nova-title">
            <ShoppingCartOutlined style={{ marginRight: '0.5rem' }} />
            NFT市场
          </h1>
          <p className="nova-subtitle">
            购买限定盲盒和稀有皮肤，打造独特收藏
          </p>
        </div>

        <div className="market-main-content">
          <Tabs
            defaultActiveKey="blindbox"
            className="market-tabs"
          >
            <Tabs.TabPane
              key="blindbox"
              tab={
                <span>
                  <GiftOutlined />
                  神秘盲盒
                </span>
              }
            >
              <div className="market-section">
                <div className="section-header">
                  <h2>🎁 神秘盲盒系列</h2>
                  <p>开启盲盒，获得随机卡牌收藏</p>
                </div>
                {renderBlindboxDemo()}
              </div>
            </Tabs.TabPane>
            
            <Tabs.TabPane
              key="skins"
              tab={
                <span>
                  <SkinOutlined />
                  限定皮肤
                </span>
              }
            >
              <div className="market-section">
                <div className="section-header">
                  <h2>✨ 限定皮肤系列</h2>
                  <p>收集专属皮肤，彰显个性风格</p>
                </div>
                {renderSkinDemo()}
              </div>
            </Tabs.TabPane>

            <Tabs.TabPane
              key="my-blindboxes"
              tab={
                <span>
                  <GiftOutlined />
                  我的盲盒 {userBlindboxes.filter(box => !box.opened).length > 0 && 
                    <span className="blindbox-count">({userBlindboxes.filter(box => !box.opened).length})</span>
                  }
                </span>
              }
            >
              <div className="market-section">
                <div className="section-header">
                  <h2>📦 我的盲盒库存</h2>
                  <p>开启您拥有的盲盒，获得专属皮肤</p>
                </div>
                {renderMyBlindboxes()}
              </div>
            </Tabs.TabPane>
          </Tabs>
        </div>
      </div>

      <Footer language={language} setLanguage={setLanguage} />
    </div>
  );
};

export default MarketPage; 